import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../AuthContext'
import { api } from '../api'
import Fox from './Fox'
import { modales } from '../contexts/modales'
import { avecAlpha } from '../lib/couleurs'

// Parcours « Devenir producteur » — modale bottom-sheet fidele au prototype v4
// (docs/manrina-app-complete-v4.tsx + DESIGN-SYSTEM.md v4.1). RINA accompagne
// chaque etape. Cablee au backend (register / claim / claim-by-id / check-similar).
//
// ANTI-DOUBLON (ADR-054 maj) : check-similar renvoie les exploitations proches
// NOMMEES (nom + commune). La personne retrouve la sienne et demande le
// rattachement. SECURITE (decision Enzo) : une org deja ATTRIBUEE (active) ne
// peut pas etre rejointe en libre-service -> on l'indique + on redirige vers
// « Contacter l'equipe » (verif manuelle). Validation finale toujours cote BO.

const C = { creme: 'rgb(var(--c-creme))', vert: 'rgb(var(--c-green))', orange: 'rgb(var(--c-primary))', teal: 'rgb(var(--c-teal))' }
const glass = {
  background: 'rgba(255,255,255,0.55)',
  border: '1px solid rgba(255,255,255,0.85)',
  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  boxShadow: '0 8px 30px rgba(7,62,61,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
}
const CONTACT_EMAIL = 'contact@manrina.fr'

const PROFILS = [
  { v: 'pro', e: '🧾', t: 'Professionnel', s: 'Agriculteur, entreprise — SIRET obligatoire', type: 'PROFESSIONNEL' },
  { v: 'particulier', e: '🏡', t: 'Particulier', s: 'Vendez le surplus de votre jardin — aucun document requis', type: 'PARTICULIER' },
  { v: 'asso', e: '🤝', t: 'Association', s: 'Collectif, structure — SIRET optionnel', type: 'ASSOCIATION' },
]
const profilToType = (p) => PROFILS.find((x) => x.v === p)?.type || 'PARTICULIER'

const VOIES = [
  { v: 'nouvelle', e: '🌱', t: 'Nouvelle inscription', s: 'Je crée mon exploitation sur Lyannaj' },
  { v: 'code', e: '🔗', t: "J'ai un code de rattachement", s: 'Code MAN-ORG fourni par Manrina' },
]

const EMPTY = { exploitation: '', commercial: '', description: '', commune: '', cp: '', adresse: '', siret: '' }
const inputStyle = { width: '100%', fontSize: 16, padding: '11px 13px', borderRadius: 13, border: '1px solid rgba(7,62,61,0.15)', background: 'rgba(255,255,255,0.7)', color: C.vert, boxSizing: 'border-box', fontFamily: 'inherit' }
const labelStyle = { fontSize: 11.5, fontWeight: 700, color: 'rgba(7,62,61,0.6)', display: 'block', marginBottom: 5 }

// Champ defini au NIVEAU MODULE (identite stable) -> l'input ne se remonte JAMAIS
// a chaque frappe (sinon perte de focus). NE PAS redefinir dans le composant.
function Field({ label, value, onChange, placeholder, area, inputMode, max }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={labelStyle}>{label}</span>
      {area ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} style={{ ...inputStyle, resize: 'none' }} />
      ) : (
        <input value={value} onChange={(e) => onChange(max ? e.target.value.slice(0, max) : e.target.value)} placeholder={placeholder} inputMode={inputMode} style={inputStyle} />
      )}
    </label>
  )
}

export default function DevenirProducteurSheet({ open, onClose }) {
  const { user } = useAuth()
  const [step, setStep] = useState('voie')      // voie | code | profil | activite | legal | recap | envoye
  const [voie, setVoie] = useState(null)        // nouvelle | code
  const [code, setCode] = useState('')
  const [profil, setProfil] = useState('pro')   // pro | particulier | asso
  const [form, setForm] = useState(EMPTY)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // anti-doublon nomme : liste d'exploitations proches (nom + commune + attribuee)
  const [matches, setMatches] = useState([])    // [{ id, nom, commune, attribuee }]
  const [orgCible, setOrgCible] = useState(null) // org choisie pour rattachement (non attribuee)
  const [ignored, setIgnored] = useState([])
  const dupTimer = useRef(null)

  if (!open) return null

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const handleClose = () => {
    clearTimeout(dupTimer.current)
    setStep('voie'); setVoie(null); setCode(''); setProfil('pro'); setForm(EMPTY)
    setError(''); setMatches([]); setOrgCible(null); setIgnored([]); setLoading(false)
    onClose()
  }

  // Recherche anti-doublon (debounce) sur le nom d'exploitation
  const runDupCheck = (nomVal) => {
    clearTimeout(dupTimer.current)
    if (!nomVal || nomVal.trim().length < 3) { setMatches([]); return }
    dupTimer.current = setTimeout(async () => {
      try {
        const r = await api.producer.checkSimilar({
          nom: nomVal, nomCommercial: form.commercial, siret: form.siret, telephone: user?.telephone,
        })
        setMatches((r?.matches || []).filter((m) => !ignored.includes(m.id)))
      } catch (_e) { /* un echec du check ne bloque pas */ }
    }, 450)
  }

  // --- Navigation (logique fidele au prototype v4) ---
  const ORDRE = ['voie', voie === 'code' ? 'code' : 'profil', 'activite', 'legal', 'recap']
  const idx = ORDRE.indexOf(step)
  const numTotal = voie === 'code' ? 2 : 5
  const numCourant = voie === 'code'
    ? (step === 'voie' ? 1 : 2)
    : (step === 'envoye' ? 5 : idx + 1)
  const titres = {
    voie: 'Devenir producteur', code: orgCible ? 'Rejoindre une exploitation' : 'Code de rattachement',
    profil: 'Votre profil', activite: 'Votre activité', legal: 'Informations légales',
    recap: 'Récapitulatif', envoye: 'Demande envoyée',
  }

  const back = () => {
    setError('')
    if (step === 'code' || step === 'profil') setStep('voie')
    else if (step === 'activite') setStep(voie === 'code' ? 'code' : 'profil')
    else if (step === 'legal') setStep('activite')
    else if (step === 'recap') setStep(profil === 'particulier' ? 'activite' : 'legal')
  }

  const peutContinuer = () => {
    if (step === 'voie') return !!voie
    if (step === 'code') return !!orgCible || /^MAN-ORG-/i.test(code.trim())
    if (step === 'activite') return form.exploitation.trim() && form.commune.trim() && form.cp.trim()
    if (step === 'legal') return profil === 'pro' ? form.siret.trim().length >= 9 : true
    return true
  }

  const next = () => {
    setError('')
    if (step === 'voie') { setStep(voie === 'code' ? 'code' : 'profil'); return }
    if (step === 'profil') { setStep('activite'); return }
    if (step === 'activite') {
      // « Continuer » = je confirme que c'est une nouvelle structure
      if (matches.length) setIgnored((p) => Array.from(new Set([...p, ...matches.map((m) => m.id)])))
      setMatches([])
      setStep(profil === 'particulier' ? 'recap' : 'legal'); return
    }
    if (step === 'legal') { setStep('recap'); return }
  }

  // « Rejoindre » depuis la liste (uniquement orgs NON attribuees)
  const demanderRattachement = (org) => {
    setOrgCible(org); setVoie('code'); setStep('code'); setError('')
  }

  const contactMailto = (org) => {
    const sujet = 'Rattachement a une exploitation Lyannaj'
    const corps = `Bonjour,\n\nJe souhaite etre rattache(e) a l'exploitation "${org.nom}"${org.commune ? ` (${org.commune})` : ''} deja presente sur Lyannaj.\nMerci de verifier mon lien avec cette exploitation.\n\n(Indiquez votre nom et votre role dans l'exploitation)`
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(corps)}`
  }

  // --- Soumissions backend ---
  const submitRegister = async () => {
    setLoading(true); setError('')
    try {
      const data = {
        nom: form.exploitation.trim(), type: profilToType(profil),
        description: form.description || undefined, ville: form.commune || undefined,
        adresseRue: form.adresse || undefined, codePostal: form.cp || undefined,
      }
      if (form.commercial) data.nomCommercial = form.commercial
      if (form.siret) data.siret = form.siret
      if (ignored.length > 0) data.ignoredDuplicates = ignored
      await api.producer.register(data)
      setStep('envoye')
    } catch (err) { setError(err.message || "Erreur lors de l'inscription") }
    finally { setLoading(false) }
  }

  const submitClaim = async () => {
    setLoading(true); setError('')
    try { await api.producer.claim(code.trim()); setStep('envoye') }
    catch (err) { setError(err.message || 'Code non reconnu') }
    finally { setLoading(false) }
  }

  const submitClaimById = async () => {
    setLoading(true); setError('')
    try { await api.producer.claimByOrgId(orgCible.id); setStep('envoye') }
    catch (err) { setError(err.message || 'Demande impossible') }
    finally { setLoading(false) }
  }

  // --- Textes RINA par etape ---
  const rinaTxt = {
    voie: "Tu veux vendre ta production ? Dis-moi : tu rejoins une exploitation déjà sur Lyannaj, ou tu crées la tienne ?",
    code: orgCible
      ? `Je t'inscris auprès de ${orgCible.nom}. L'administrateur de l'exploitation validera ton arrivée.`
      : "Saisis le code que l'équipe Manrina t'a transmis — il te rattache à ton exploitation.",
    profil: "Choisis le statut qui te correspond. Pas d'inquiétude : on peut ajuster plus tard si besoin.",
    activite: "Présente ton exploitation. Si un nom proche existe déjà, je te le signale pour éviter les doublons.",
    legal: profil === 'pro'
      ? 'Le SIRET nous sert à établir tes factures et tes versements. Il reste entre nous.'
      : 'Tu peux ajouter un SIRET si tu en as un — sinon, on continue tranquillement.',
    recap: "On y est presque ! Vérifie tes infos, et j'envoie ta demande à l'équipe pour validation.",
  }[step]

  const ctaVert = (label, onClick, enabled = true) => (
    <button onClick={onClick} disabled={!enabled || loading}
      style={{ width: '100%', padding: 15, borderRadius: 16, border: 'none', background: (enabled && !loading) ? C.vert : 'rgba(7,62,61,0.2)', color: C.creme, fontSize: 15, fontWeight: 700, cursor: (enabled && !loading) ? 'pointer' : 'default' }}>
      {loading ? '…' : label}
    </button>
  )

  return createPortal(
    <div onClick={handleClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(7,62,61,0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="panel-slide-up"
        style={{ width: '100%', maxWidth: 430, height: '85vh', background: C.creme, borderRadius: '28px 28px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 -10px 50px rgba(4,25,24,0.3)' }}>

        {/* Header une ligne */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: '1px solid rgba(7,62,61,0.08)' }}>
          {step !== 'voie' && step !== 'envoye' && (
            <button onClick={back} aria-label="Retour" style={{ background: 'none', border: 'none', fontSize: 22, color: C.vert, cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
          )}
          <p style={{ flex: 1, fontFamily: "'Instrument Serif',serif", fontSize: 20, color: C.vert }}>{titres[step]}</p>
          {step !== 'envoye' && <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(7,62,61,0.4)' }}>Étape {numCourant}/{numTotal}</span>}
          <button onClick={handleClose} aria-label="Fermer" style={{ background: 'none', border: 'none', fontSize: 24, color: 'rgba(7,62,61,0.4)', cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {/* Corps scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {rinaTxt && (
            <div style={{ ...glass, borderRadius: 20, padding: '13px 15px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12, background: 'linear-gradient(120deg, rgba(255,255,255,0.6), rgba(244,137,83,0.16))' }}>
              <span style={{ width: 42, height: 42, borderRadius: 14, flexShrink: 0, background: 'linear-gradient(135deg,rgba(244,137,83,0.35),rgba(255,255,255,0.4))', border: '1px solid rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Fox size={26} />
              </span>
              <p style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.vert, lineHeight: 1.45 }}>{rinaTxt}</p>
            </div>
          )}

          {step === 'voie' && VOIES.map((o) => (
            <button key={o.v} onClick={() => setVoie(o.v)}
              style={{ ...glass, width: '100%', borderRadius: 18, padding: 16, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 13, textAlign: 'left', cursor: 'pointer', border: voie === o.v ? `2px solid ${C.orange}` : '1px solid rgba(255,255,255,0.85)' }}>
              <span style={{ fontSize: 24 }}>{o.e}</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14.5, fontWeight: 700, color: C.vert }}>{o.t}</p>
                <p style={{ fontSize: 11.5, color: 'rgba(7,62,61,0.55)' }}>{o.s}</p>
              </div>
              <span style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, border: voie === o.v ? `6px solid ${C.orange}` : '2px solid rgba(7,62,61,0.25)' }} />
            </button>
          ))}

          {step === 'code' && (
            <>
              {orgCible ? (
                <>
                  <div style={{ ...glass, borderRadius: 18, padding: 16, display: 'flex', alignItems: 'center', gap: 13, border: `1px solid ${avecAlpha(C.orange, 0.33)}` }}>
                    <span style={{ width: 46, height: 46, borderRadius: 15, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(20,184,166,0.14)', border: '1px solid rgba(255,255,255,0.7)' }}>
                      <span style={{ fontSize: 22 }}>🌿</span>
                    </span>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 15, fontWeight: 700, color: C.vert }}>{orgCible.nom}</p>
                      {orgCible.commune && <p style={{ fontSize: 12, color: 'rgba(7,62,61,0.55)' }}>📍 {orgCible.commune}</p>}
                    </div>
                  </div>
                  <button onClick={() => { setOrgCible(null); setVoie('nouvelle'); setStep('activite') }}
                    style={{ marginTop: 12, fontSize: 12, fontWeight: 700, color: C.orange, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Ce n'est pas la mienne — créer une nouvelle exploitation
                  </button>
                </>
              ) : (
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="MAN-ORG-XXXXX" autoFocus
                  style={{ ...inputStyle, fontSize: 18, fontWeight: 700, letterSpacing: 1, textAlign: 'center', padding: 15, borderRadius: 15 }} />
              )}
              <div style={{ ...glass, borderRadius: 14, padding: '12px 14px', marginTop: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16 }}>ℹ️</span>
                <p style={{ fontSize: 11.5, color: 'rgba(7,62,61,0.6)', lineHeight: 1.5 }}>Vous rejoindrez l'exploitation avec le rôle <strong>Membre</strong>. L'administrateur de l'exploitation validera votre arrivée.</p>
              </div>
            </>
          )}

          {step === 'profil' && PROFILS.map((o) => (
            <button key={o.v} onClick={() => setProfil(o.v)}
              style={{ ...glass, width: '100%', borderRadius: 18, padding: '15px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 13, textAlign: 'left', cursor: 'pointer', border: profil === o.v ? `2px solid ${C.orange}` : '1px solid rgba(255,255,255,0.85)' }}>
              <span style={{ fontSize: 23 }}>{o.e}</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14.5, fontWeight: 700, color: C.vert }}>{o.t}</p>
                <p style={{ fontSize: 11.5, color: 'rgba(7,62,61,0.55)', lineHeight: 1.45 }}>{o.s}</p>
              </div>
              <span style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, border: profil === o.v ? `6px solid ${C.orange}` : '2px solid rgba(7,62,61,0.25)' }} />
            </button>
          ))}

          {step === 'activite' && (
            <>
              <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={labelStyle}>Nom de l'exploitation *</span>
                <input value={form.exploitation} onChange={(e) => { set('exploitation', e.target.value); runDupCheck(e.target.value) }} placeholder="Ex. Ferme du Morne Vert" style={inputStyle} />
              </label>

              {matches.length > 0 && (
                <div style={{ borderRadius: 14, padding: '13px 15px', marginBottom: 12, background: 'rgba(244,137,83,0.1)', border: '1px solid rgba(244,137,83,0.4)' }}>
                  <p style={{ fontSize: 12.5, fontWeight: 700, color: '#c0492f', marginBottom: 3 }}>⚠️ {matches.length > 1 ? 'Des exploitations proches existent déjà' : 'Une exploitation proche existe déjà'}</p>
                  <p style={{ fontSize: 12, color: 'rgba(7,62,61,0.7)', lineHeight: 1.5, marginBottom: 8 }}>La vôtre est peut-être déjà sur Lyannaj.</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {matches.map((m) => (
                      <div key={m.id} style={{ ...glass, padding: '10px 12px', borderRadius: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 18 }}>🌿</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 13, fontWeight: 700, color: C.vert, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.nom}</p>
                            <p style={{ fontSize: 11, color: 'rgba(7,62,61,0.55)' }}>{m.commune ? `📍 ${m.commune} · ` : ''}{m.attribuee ? 'déjà sur Lyannaj' : 'non rattachée'}</p>
                          </div>
                        </div>
                        {m.attribuee ? (
                          <a href={contactMailto(m)}
                            style={{ display: 'block', textAlign: 'center', marginTop: 8, padding: '8px 0', borderRadius: 99, background: 'rgba(7,62,61,0.08)', color: C.vert, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                            ✉️ Contacter l'équipe pour la rejoindre
                          </a>
                        ) : (
                          <button onClick={() => demanderRattachement(m)}
                            style={{ width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 99, border: 'none', background: C.orange, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            Demander un rattachement
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Field label="Nom commercial" value={form.commercial} onChange={(v) => set('commercial', v)} placeholder="Optionnel — affiché aux clients" />
              <Field label="Description" value={form.description} onChange={(v) => set('description', v)} placeholder="Vos produits, votre démarche…" area />
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 2 }}><Field label="Commune *" value={form.commune} onChange={(v) => set('commune', v)} placeholder="Rivière-Salée" /></div>
                <div style={{ flex: 1 }}><Field label="Code postal *" value={form.cp} onChange={(v) => set('cp', v)} placeholder="97215" inputMode="numeric" max={5} /></div>
              </div>
              <Field label="Adresse" value={form.adresse} onChange={(v) => set('adresse', v)} placeholder="Lieu-dit, voie…" />
            </>
          )}

          {step === 'legal' && (
            <>
              <Field label={profil === 'pro' ? 'SIRET *' : 'SIRET (optionnel)'} value={form.siret} onChange={(v) => set('siret', v)} placeholder="14 chiffres" inputMode="numeric" max={14} />
              <div style={{ ...glass, borderRadius: 14, padding: '12px 14px', marginTop: 4, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16 }}>🔒</span>
                <p style={{ fontSize: 11.5, color: 'rgba(7,62,61,0.6)', lineHeight: 1.5 }}>Vos informations légales ne sont jamais publiques. Elles servent uniquement à la facturation et aux versements.</p>
              </div>
            </>
          )}

          {step === 'recap' && (
            <>
              <div style={{ ...glass, borderRadius: 18, padding: 16, marginBottom: 14 }}>
                {[
                  ['Profil', PROFILS.find((p) => p.v === profil)?.t],
                  ['Exploitation', form.exploitation || '—'],
                  ['Nom commercial', form.commercial || '—'],
                  ['Commune', `${form.commune || '—'}${form.cp ? ' · ' + form.cp : ''}`],
                  ...(profil !== 'particulier' ? [['SIRET', form.siret || '—']] : []),
                ].map(([k, v], i, a) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: i < a.length - 1 ? '1px solid rgba(7,62,61,0.07)' : 'none' }}>
                    <span style={{ fontSize: 12.5, color: 'rgba(7,62,61,0.5)' }}>{k}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: C.vert, textAlign: 'right' }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderRadius: 14, padding: '13px 15px', background: 'rgba(20,184,166,0.1)', border: '1px solid rgba(20,184,166,0.35)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16 }}>👀</span>
                <p style={{ fontSize: 12, color: 'rgba(7,62,61,0.7)', lineHeight: 1.5 }}>Votre demande sera <strong>examinée par l'équipe Manrina</strong> avant activation. Vous serez prévenu dès la validation.</p>
              </div>
            </>
          )}

          {step === 'envoye' && (
            <div style={{ textAlign: 'center', paddingTop: 30 }}>
              <div style={{ fontSize: 54, marginBottom: 12 }}>🎉</div>
              <p style={{ fontFamily: "'Instrument Serif',serif", fontSize: 24, color: C.vert, marginBottom: 8 }}>Demande envoyée !</p>
              <p style={{ fontSize: 13.5, color: 'rgba(7,62,61,0.6)', lineHeight: 1.5, padding: '0 10px' }}>L'équipe Manrina examine votre demande. Vous recevrez une notification dès qu'elle est validée.</p>
            </div>
          )}

          {error && (
            <p style={{ marginTop: 14, fontSize: 12.5, fontWeight: 600, color: '#c0492f', textAlign: 'center' }}>{error}</p>
          )}
        </div>

        {/* CTA fixe */}
        <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(7,62,61,0.08)' }}>
          {step === 'envoye' ? ctaVert('Terminer', handleClose)
            : step === 'recap' ? (
              <button onClick={submitRegister} disabled={loading}
                style={{ width: '100%', padding: 15, borderRadius: 16, border: 'none', background: C.orange, color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
                {loading ? '…' : 'Envoyer ma demande'}
              </button>
            )
            : step === 'code' ? (orgCible
              ? ctaVert(`Demander à rejoindre ${orgCible.nom}`, submitClaimById)
              : ctaVert("Rejoindre l'exploitation", submitClaim, peutContinuer()))
            : ctaVert('Continuer', next, peutContinuer())}
        </div>
      </div>
    </div>,
    document.body,
  )
}

// Ouvreur imperatif (ADR-066). Le montage a l'ouverture repart d'un wizard
// NEUF a chaque fois (avant, l'etat de l'assistant survivait a la fermeture).
// `persisteALaNavigation` : requis pour l'entree par l'URL /devenir-producteur,
// qui ouvre la feuille PUIS redirige vers / — sans lui, la regle « changer de
// route ferme la pile » la refermerait aussitot.
export function ouvrirDevenirProducteur({ persisteALaNavigation = false } = {}) {
  modales.ouvrir({
    id: 'devenir-producteur',
    persisteALaNavigation,
    contenu: ({ fermer }) => <DevenirProducteurSheet open onClose={fermer} />,
  })
}
